/**
 * ApprovedReportsTable — list of all approved saved reports with bulk-send and navigation.
 * Stage 3 UI extraction from ReportsPage.js
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { History, Mail, Search, FileText, BarChart3, AlertCircle } from 'lucide-react';

const ApprovedReportsTable = ({
  activeReport,
  savedReports,
  loadingSaved,
  savedSearch, setSavedSearch,
  selectedReportIds, setSelectedReportIds,
  toggleSelectReport,
  selectedApproved,
  bulkSending,
  handleBulkSend,
  navigate,
}) => {
  const filtered = savedReports
    .filter(r => !activeReport || r.report_type === activeReport)
    .filter(r => r.report_status !== 'draft')
    .filter(r => {
      if (!savedSearch.trim()) return true;
      const q = savedSearch.toLowerCase();
      const approvedDateStr = (r.approved_at || r.saved_at)
        ? new Date(r.approved_at || r.saved_at).toLocaleDateString()
        : '';
      const statusText = [
        'approved',
        r.emailed ? 'emailed' : '',
        r.published_to_portal ? 'published' : '',
      ].join(' ');
      return (r.report_name || '').toLowerCase().includes(q)
        || (r.crm_entity_name || '').toLowerCase().includes(q)
        || (r.approved_by || '').toLowerCase().includes(q)
        || (r.saved_by || '').toLowerCase().includes(q)
        || approvedDateStr.toLowerCase().includes(q)
        || (r.saved_at || '').includes(q)
        || (r.report_type || '').toLowerCase().includes(q)
        || statusText.includes(q);
    });

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base">Approved Reports</CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {activeReport === 'daily-sale-updates' && selectedApproved.length > 0 && (
              <Button size="sm" className="h-8 text-xs bg-primary hover:bg-primary/90 text-white" onClick={handleBulkSend} disabled={bulkSending}>
                <Mail className="w-3 h-3 mr-1" /> {bulkSending ? 'Sending...' : `Send to Owners (${selectedApproved.length})`}
              </Button>
            )}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input className="pl-9 h-8 text-sm" placeholder="Search saved reports..." value={savedSearch} onChange={e => setSavedSearch(e.target.value)} />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loadingSaved ? (
          <div className="flex justify-center py-8"><BarChart3 className="w-5 h-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/20" />
            <p className="text-sm text-muted-foreground">
              {savedSearch ? 'No matching reports' : 'No approved reports yet. Run a report and click Approve Report.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <Table>
              <TableHeader>
                <TableRow>
                {activeReport === 'daily-sale-updates' && (
                  <TableHead className="w-8">
                    <input type="checkbox" className="w-3.5 h-3.5"
                      checked={
                        filtered.filter(r => !r.emailed && !r.published_to_portal).length > 0 &&
                        filtered.filter(r => !r.emailed && !r.published_to_portal).every(r => selectedReportIds.includes(r.id))
                      }
                      onChange={() => {
                        const selectable = filtered.filter(r => !r.emailed && !r.published_to_portal);
                        const allSel = selectable.every(r => selectedReportIds.includes(r.id));
                        setSelectedReportIds(allSel ? [] : selectable.map(r => r.id));
                      }} />
                  </TableHead>
                )}
                <TableHead>Report</TableHead>
                {activeReport !== 'departure-form' && <TableHead>Account</TableHead>}
                <TableHead>Approved</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(r => {
                const alreadySent = r.emailed || r.published_to_portal;
                const noEmail = r.owner_has_email === false;
                return (
                  <TableRow key={r.id} className="hover:bg-muted/50">
                    {activeReport === 'daily-sale-updates' && (
                      <TableCell onClick={e => { if (!alreadySent) { e.stopPropagation(); toggleSelectReport(r.id); } }}>
                        <input type="checkbox" className="w-3.5 h-3.5"
                          checked={selectedReportIds.includes(r.id)}
                          disabled={alreadySent}
                          readOnly
                          title={alreadySent ? 'Already sent/published' : ''}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium cursor-pointer" onClick={() => navigate(`/reports/saved/${r.id}`)}>{r.report_name}</TableCell>
                    {activeReport !== 'departure-form' && (
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1.5">
                        <span>{r.crm_entity_name || '-'}</span>
                        {activeReport === 'daily-sale-updates' && noEmail && (
                          <span title="No primary email set for this owner">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    )}
                    <TableCell className="text-sm">{(r.approved_at || r.saved_at) ? new Date(r.approved_at || r.saved_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-sm">{r.approved_by || r.saved_by || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Badge className="bg-green-100 text-green-700 text-xs pointer-events-none">Approved</Badge>
                        {r.emailed && <Badge className="bg-blue-100 text-blue-700 text-xs pointer-events-none">Emailed</Badge>}
                        {r.published_to_portal && <Badge className="bg-purple-100 text-purple-700 text-xs pointer-events-none">Published</Badge>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApprovedReportsTable;
